import React, { useState, useEffect } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import {
  ChatCompletionRequest,
  ChatMessage,
  ToolCall,
} from "../../model_gen/chat";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";
import { TestSetService } from "../../services/TestSetService";
import { RequestsService } from "../../services/RequestsService";
import { 
  CheckIcon, 
  XIcon, 
  ChevronDownIcon, 
  PlusIcon,
  MessageSquareIcon,
  TerminalIcon,
  UserIcon,
  BotIcon,
  SettingsIcon
} from "lucide-react";
import { cn } from "../../lib/utils";

interface RequestViewProps {
  request?: ChatCompletionRequest | null;
  apiKey: ApiKeyOrGetter;
  isEditable?: boolean;
  testSetId?: string;
  testSetRequestId?: string | null;
  onUpdated?: (updatedRequest: ChatCompletionRequest) => void;
  systemPrompt?: string;
  requestId?: string;
  applicationId?: string;
  applicationActionId?: string;
}

const RequestView: React.FC<RequestViewProps> = ({
  request: initialRequest,
  apiKey,
  isEditable = false,
  testSetId,
  testSetRequestId,
  onUpdated,
  systemPrompt,
  requestId,
  applicationId,
  applicationActionId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<ChatCompletionRequest | null>(null);
  const [editedToolCalls, setEditedToolCalls] = useState<ToolCall[] | null>(null);
  const [showAddToolCall, setShowAddToolCall] = useState(false);
  const [newToolCall, setNewToolCall] = useState({ name: "", arguments: "" });
  const [requestData, setRequestData] = useState<ChatCompletionRequest | null>(initialRequest);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());
  const [middleMessagesExpanded, setMiddleMessagesExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (initialRequest) {
      setRequestData(initialRequest);
      // Auto-expand the last assistant message
      const lastAssistantIndex = initialRequest.params?.messages
        ?.map((msg, index) => ({ role: msg.role, index }))
        .filter(({ role }) => role === "assistant")
        .pop()?.index;
      
      if (lastAssistantIndex !== undefined) {
        setExpandedMessages(new Set([lastAssistantIndex]));
      }
      return;
    }

    if (requestId && applicationId && applicationActionId) {
      RequestsService.getRequest(
        applicationId,
        applicationActionId,
        requestId,
        setIsLoading,
        apiKey
      ).subscribe(
        (data: ChatCompletionRequest) => {
          setRequestData(data);
          // Auto-expand the last assistant message
          const lastAssistantIndex = data.params?.messages
            ?.map((msg, index) => ({ role: msg.role, index }))
            .filter(({ role }) => role === "assistant")
            .pop()?.index;
          
          if (lastAssistantIndex !== undefined) {
            setExpandedMessages(new Set([lastAssistantIndex]));
          }
        },
        (error: any) => {
          console.error("Error fetching request:", error);
          toast.error("Failed to fetch request details");
        }
      );
    }
  }, [initialRequest, requestId, applicationId, applicationActionId, apiKey]);

  const request = requestData;

  const getMessageContent = (msg: ChatMessage, index: number) => {
    const isLastMessage = index === (request?.params?.messages?.length || 0) - 1;
    if (isLastMessage) {
      if (pendingRequest && editedContent !== null) {
        return editedContent;
      }
      if (request?.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.content;
      }
      const testResponse = request?.metadata?._maitai_test_request_response;
      if (testResponse !== undefined && testResponse !== null) {
        return testResponse;
      }
      return msg.content;
    }
    return msg.content;
  };

  const handleContentChange = (index: number, newContent: string) => {
    if (!request || !isEditable) return;
    const updatedRequest = JSON.parse(JSON.stringify(request));
    setEditedContent(newContent);
    setPendingRequest(updatedRequest);
  };

  const handleToolCallChange = (toolCallIndex: number, field: string, value: string) => {
    if (!request || !isEditable || editedToolCalls === null) return;

    if (!pendingRequest) {
      const updatedRequest = JSON.parse(JSON.stringify(request));
      setPendingRequest(updatedRequest);
    }

    const updatedToolCalls = [...editedToolCalls];
    if (field === "name") {
      updatedToolCalls[toolCallIndex].function.name = value;
    } else if (field === "arguments") {
      updatedToolCalls[toolCallIndex].function.arguments = value;
    }

    setEditedToolCalls(updatedToolCalls);
  };

  const handleAddToolCall = () => {
    if (!editedToolCalls && request?.params?.messages) {
      const lastAssistantMessage = request.params.messages
        .filter((m) => m.role === "assistant")
        .pop();
      const currentToolCalls = lastAssistantMessage?.tool_calls || [];
      setEditedToolCalls([...currentToolCalls]);
    }
    setShowAddToolCall(true);
  };

  const confirmAddToolCall = () => {
    if (!editedToolCalls) return;

    const newToolCallObj = {
      id: `call_${Date.now()}`,
      type: "function" as const,
      function: {
        name: newToolCall.name,
        arguments: newToolCall.arguments,
      },
    };

    setEditedToolCalls([...editedToolCalls, newToolCallObj]);
    setNewToolCall({ name: "", arguments: "" });
    setShowAddToolCall(false);
  };

  const cancelAddToolCall = () => {
    setNewToolCall({ name: "", arguments: "" });
    setShowAddToolCall(false);
  };

  const handleSave = () => {
    if (!pendingRequest) return;

    const updatedRequest = JSON.parse(JSON.stringify(pendingRequest));
    const lastAssistantIndex = updatedRequest.params.messages
      .map((m: ChatMessage, i: number) => ({ role: m.role, index: i }))
      .filter((m: any) => m.role === "assistant")
      .pop()?.index;

    if (lastAssistantIndex !== undefined) {
      if (editedContent !== null) {
        updatedRequest.metadata._maitai_test_request_response = editedContent;
      }
      if (editedToolCalls !== null) {
        updatedRequest.metadata._maitai_test_request_response_tool_calls = editedToolCalls;
      }
    }

    if (!testSetRequestId) {
      onUpdated?.(updatedRequest);
      setEditedContent(null);
      setEditedToolCalls(null);
      setPendingRequest(null);
      return;
    }

    setIsLoading(true);

    TestSetService.updateTestSetRequestResponse(
      testSetId!,
      updatedRequest.request_id,
      {
        test_set_request_id: testSetRequestId,
        message_content: editedContent,
        tool_calls: editedToolCalls,
      },
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        if (onUpdated) {
          onUpdated(updatedRequest);
        }
        setEditedContent(null);
        setEditedToolCalls(null);
        setPendingRequest(null);
        toast.success("Message updated successfully");
      },
      (error: any) => {
        console.error("Failed to update message:", error);
        toast.error("Failed to update message");
      }
    );
  };

  const handleCancel = () => {
    setEditedContent(null);
    setEditedToolCalls(null);
    setPendingRequest(null);
    setShowAddToolCall(false);
  };

  const isMessageEditable = (msg: ChatMessage, index: number) => {
    if (!isEditable) return false;
    const lastAssistantIndex = request?.params?.messages
      ?.map((m, i) => ({ role: m.role, index: i }))
      .filter((m) => m.role === "assistant")
      .pop()?.index;
    return index === lastAssistantIndex;
  };

  const isMessageEdited = (msg: ChatMessage, index: number) => {
    const isLastMessage = index === (request?.params?.messages?.length || 0) - 1;
    return (
      isLastMessage &&
      (request?.metadata?._maitai_annotation_label !== undefined ||
        request?.metadata?._maitai_test_request_response !== undefined ||
        (request?.metadata?._maitai_test_request_response_tool_calls &&
          (request.metadata._maitai_test_request_response_tool_calls as ToolCall[]).length > 0))
    );
  };

  const getToolCalls = (msg: ChatMessage) => {
    if (editedToolCalls !== null && isMessageEditable(msg, request?.params?.messages?.indexOf(msg) || 0)) {
      return editedToolCalls;
    }
    if (request?.metadata?._maitai_test_request_response_tool_calls) {
      return request.metadata._maitai_test_request_response_tool_calls as ToolCall[];
    }
    return msg?.tool_calls || [];
  };

  const renderMessageContent = (message: ChatMessage) => {
    if (message.role === "system" && systemPrompt) {
      return systemPrompt;
    }
    let content = getMessageContent(message, request?.params?.messages?.indexOf(message) || 0) as string;

    if (
      message?.tool_calls &&
      !isMessageEditable(message, request?.params?.messages?.indexOf(message) || 0)
    ) {
      if (content) {
        content += "\n\n";
      }
      content += "Tool calls:\n";
      if (message?.tool_calls?.length > 0) {
        content += renderToolCalls(message.tool_calls);
      }
    }

    return content;
  };

  const toggleMessageExpansion = (index: number) => {
    setExpandedMessages(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  };

  const getMessageCategories = (messages: ChatMessage[]) => {
    if (!messages || messages.length === 0) return { systemMessage: null, middleMessages: [], lastAssistantMessage: null };

    const systemMessage = messages.find((msg, index) => msg.role === "system" && index === 0) || null;
    const lastAssistantIndex = messages.map((msg, index) => ({ msg, index }))
      .filter(({ msg }) => msg.role === "assistant")
      .pop()?.index;

    const middleMessages: { msg: ChatMessage; index: number }[] = [];
    
    messages.forEach((msg, index) => {
      // Skip system message (index 0)
      if (index === 0 && msg.role === "system") return;
      
      // Skip last assistant message
      if (index === lastAssistantIndex) return;
      
      middleMessages.push({ msg, index });
    });

    const lastAssistantMessage = lastAssistantIndex !== undefined 
      ? { msg: messages[lastAssistantIndex], index: lastAssistantIndex }
      : null;

    return { systemMessage: systemMessage ? { msg: systemMessage, index: 0 } : null, middleMessages, lastAssistantMessage };
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "system":
        return <SettingsIcon className="w-4 h-4" />;
      case "user":
        return <UserIcon className="w-4 h-4" />;
      case "assistant":
        return <BotIcon className="w-4 h-4" />;
      case "tool":
        return <TerminalIcon className="w-4 h-4" />;
      default:
        return <MessageSquareIcon className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "system":
        return "text-orange-600";
      case "user":
        return "text-blue-600";
      case "assistant":
        return "text-maitai-green";
      case "tool":
        return "text-purple-600";
      default:
        return "text-muted-foreground";
    }
  };

  const renderCompactMessage = (msg: ChatMessage, index: number) => {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className={cn("flex items-center gap-2", getRoleColor(msg.role))}>
            {getRoleIcon(msg.role)}
            <span className="text-xs font-gc-med capitalize">
              {msg.role}
              {msg.role === "system" && systemPrompt && msg.content !== systemPrompt && "*"}
              {isMessageEdited(msg, index) && "*"}
            </span>
          </div>
        </div>
        <div className="bg-muted/30 rounded px-3 py-2">
          <pre className="text-xs font-mono text-foreground/90 whitespace-pre-wrap break-words leading-relaxed">
            {renderMessageContent(msg)}
          </pre>
        </div>
      </div>
    );
  };

  const renderSystemMessage = (msg: ChatMessage, index: number) => {
    const isExpanded = expandedMessages.has(index);
    
    return (
      <Card className="border-border/50">
        <CardHeader 
          className="pb-4 cursor-pointer hover:bg-accent/20 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleMessageExpansion(index);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={cn("flex items-center gap-2", getRoleColor(msg.role))}>
                {getRoleIcon(msg.role)}
                <span className="text-sm font-gc-med capitalize">
                  {msg.role}
                  {msg.role === "system" && systemPrompt && msg.content !== systemPrompt && "*"}
                  {isMessageEdited(msg, index) && "*"}
                </span>
              </div>
            </div>
            <ChevronDownIcon 
              className={cn(
                "w-4 h-4 text-muted-foreground transition-transform",
                isExpanded ? "rotate-180" : ""
              )} 
            />
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="pt-0">
            {renderCompactMessage(msg, index)}
          </CardContent>
        )}
      </Card>
    );
  };

  const renderMessage = (msg: ChatMessage, index: number) => {
    const isExpanded = expandedMessages.has(index);
    const isEditable = isMessageEditable(msg, index);
    const isEdited = isMessageEdited(msg, index);

    return (
      <Card key={index} className="border-border/50">
        <CardHeader 
          className="pb-4 cursor-pointer hover:bg-accent/20 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleMessageExpansion(index);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={cn("flex items-center gap-2", getRoleColor(msg.role))}>
                {getRoleIcon(msg.role)}
                <span className="text-sm font-gc-med capitalize">
                  {msg.role}
                  {msg.role === "system" && systemPrompt && msg.content !== systemPrompt && "*"}
                  {isEdited && "*"}
                </span>
              </div>
            </div>
            <ChevronDownIcon 
              className={cn(
                "w-4 h-4 text-muted-foreground transition-transform",
                isExpanded ? "rotate-180" : ""
              )} 
            />
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="pt-0">
            {isEditable ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med text-foreground">Message Content</Label>
                  <Textarea
                    value={getMessageContent(msg, index) as string}
                    onChange={(e) => handleContentChange(index, e.target.value)}
                    className="min-h-[100px] max-h-[300px] font-mono text-sm"
                    placeholder="Enter message content..."
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-gc-med text-foreground">Tool Calls</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!pendingRequest) {
                          const updatedRequest = JSON.parse(JSON.stringify(request));
                          setPendingRequest(updatedRequest);
                        }
                        handleAddToolCall();
                      }}
                      className="gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Tool Call
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {getToolCalls(msg).map((toolCall: any, toolIndex: number) => (
                      <Card key={toolCall.id || toolIndex} className="border-border/50">
                        <CardContent className="p-4 space-y-3">
                          <div className="space-y-2">
                            <Label className="text-xs font-gc-med text-foreground">Function Name</Label>
                            <Input
                              value={toolCall.function.name}
                              onChange={(e) => {
                                if (editedToolCalls === null) {
                                  setEditedToolCalls(getToolCalls(msg));
                                }
                                handleToolCallChange(toolIndex, "name", e.target.value);
                              }}
                              className="text-sm font-mono"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs font-gc-med text-foreground">Arguments</Label>
                            <Textarea
                              value={toolCall.function.arguments}
                              onChange={(e) => {
                                if (editedToolCalls === null) {
                                  setEditedToolCalls(getToolCalls(msg));
                                }
                                handleToolCallChange(toolIndex, "arguments", e.target.value);
                              }}
                              className="min-h-[60px] max-h-[100px] text-sm font-mono"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {showAddToolCall && (
                      <Card className="border-maitai-green/50 bg-maitai-green/5">
                        <CardContent className="p-4 space-y-3">
                          <div className="space-y-2">
                            <Label className="text-xs font-gc-med text-foreground">Function Name</Label>
                            <Input
                              value={newToolCall.name}
                              onChange={(e) => setNewToolCall({ ...newToolCall, name: e.target.value })}
                              className="text-sm font-mono"
                              placeholder="function_name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs font-gc-med text-foreground">Arguments</Label>
                            <Textarea
                              value={newToolCall.arguments}
                              onChange={(e) => setNewToolCall({ ...newToolCall, arguments: e.target.value })}
                              className="min-h-[60px] text-sm font-mono"
                              placeholder='{"key": "value"}'
                            />
                          </div>
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={cancelAddToolCall}>
                              Cancel
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={confirmAddToolCall}
                              disabled={!newToolCall.name.trim()}
                              className="border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white"
                            >
                              Add
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-card/50 border border-border rounded-lg p-4">
                  <pre className="text-sm font-mono text-foreground whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                    {renderMessageContent(msg)}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  if (isLoading && !request) {
    return (
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-20" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <Separator />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-16 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!request) {
    return (
      <Card className="border-border">
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <MessageSquareIcon className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm font-gc-reg">No request found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!request.params?.messages?.length) {
    return (
      <Card className="border-border">
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <MessageSquareIcon className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm font-gc-reg">Request messages empty</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  let parsedMetadata = null;
  try {
    parsedMetadata = request.metadata?._maitai_request_meta
      ? JSON.parse(request.metadata._maitai_request_meta as string)
      : null;
  } catch (e) {
    console.error("Failed to parse metadata:", e);
  }

  return (
    <Card 
      className="border-border"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <MessageSquareIcon className="w-5 h-5 text-maitai-green" />
            Request
          </CardTitle>
          {isEditable && pendingRequest && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="gap-1 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="w-4 h-4" />
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={isLoading}
                className="gap-1 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white"
              >
                <CheckIcon className="w-4 h-4" />
                Save
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {parsedMetadata?.summary && (
          <>
            <div className="bg-card/50 border border-border rounded-lg p-4">
              <h4 className="text-sm font-gc-med text-foreground mb-2">Summary</h4>
              <p className="text-sm text-muted-foreground font-gc-reg">{parsedMetadata.summary}</p>
            </div>
            <Separator />
          </>
        )}

        <div className="space-y-3">
          {(() => {
            const { systemMessage, middleMessages, lastAssistantMessage } = getMessageCategories(request.params.messages);
            
            return (
              <>
                {/* System Message */}
                {systemMessage && renderSystemMessage(systemMessage.msg, systemMessage.index)}
                
                {/* Middle Messages - Collapsible */}
                {middleMessages.length > 0 && (
                  <Card className="border-border/50">
                    <CardHeader 
                      className="pb-4 cursor-pointer hover:bg-accent/20 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setMiddleMessagesExpanded(!middleMessagesExpanded);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MessageSquareIcon className="w-4 h-4" />
                            <span className="text-sm font-gc-med">
                              {middleMessages.length} middle message{middleMessages.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                        <ChevronDownIcon 
                          className={cn(
                            "w-4 h-4 text-muted-foreground transition-transform",
                            middleMessagesExpanded ? "rotate-180" : ""
                          )} 
                        />
                      </div>
                    </CardHeader>
                    {middleMessagesExpanded && (
                      <CardContent className="pt-0">
                        <div className="space-y-4">
                          {middleMessages.map(({ msg, index }) => (
                            <div key={index} className="space-y-1">
                              <div className="flex items-center gap-2">
                                <div className={cn("flex items-center gap-2", getRoleColor(msg.role))}>
                                  {getRoleIcon(msg.role)}
                                  <span className="text-xs font-gc-med capitalize">
                                    {msg.role}
                                    {msg.role === "system" && systemPrompt && msg.content !== systemPrompt && "*"}
                                    {isMessageEdited(msg, index) && "*"}
                                  </span>
                                </div>
                              </div>
                              <div className="bg-muted/30 rounded px-3 py-2">
                                <pre className="text-xs font-mono text-foreground/90 whitespace-pre-wrap break-words leading-relaxed">
                                  {renderMessageContent(msg)}
                                </pre>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}
                
                {/* Last Assistant Message */}
                {lastAssistantMessage && renderMessage(lastAssistantMessage.msg, lastAssistantMessage.index)}
              </>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
};

const renderToolCalls = (toolCalls: ToolCall[]): string => {
  return toolCalls
    .map((toolCall: ToolCall) => `${toolCall.function.name}(${toolCall.function.arguments})`)
    .join("\n");
};

export default RequestView;
